import {
  ActivityIndicator,
  Keyboard,
  StyleSheet,
  useWindowDimensions,
} from 'react-native'
import Animated, {
  Easing,
  Keyframe,
  LinearTransition,
} from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { PillButton } from '../components/PillButton'
import { useFullHeightStep } from '../flow/flowStepHeight'
import { ReceiveAmountSummary } from '../components/ReceiveAmountSummary'
import { ReceiveAsRow } from '../components/ReceiveAsRow'
import { TopBar } from '../components/TopBar'
import { WalletAddressInput } from '../components/WalletAddressInput'
import type { WithdrawalToken } from '../flow/withdrawalFlowConfig'
import { useKeyboardHeight } from '../flow/useKeyboardHeight'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'
import { footerBottomMargin } from '../utils/safeArea'

const AnimatedBox = Animated.createAnimatedComponent(Box)

// Withdrawing commits the screen: the nav and the inputs above the figure
// have nothing left to do, so they clear out and let it take the space.
const SUBMIT_TRANSITION_DURATION = 380
const SUBMIT_TRANSITION_EASING = Easing.bezier(0.16, 1, 0.3, 1)
const SUBMIT_TRAVEL = 24
const FADE_SLIDE_UP_EXITING = new Keyframe({
  0: { opacity: 1, transform: [{ translateY: 0 }] },
  100: {
    opacity: 0,
    transform: [{ translateY: -SUBMIT_TRAVEL }],
    easing: SUBMIT_TRANSITION_EASING,
  },
}).duration(SUBMIT_TRANSITION_DURATION)
// The figure rides into the space the nav and inputs leave. Every box between
// the screen and it shares this clock: a layout animation interpolates only its
// own view's parent-relative origin, so an unanimated box in between snaps its
// whole delta on the first frame and the figure snaps with it.
const SUBMIT_TRANSITION = LinearTransition.duration(
  SUBMIT_TRANSITION_DURATION,
).easing(SUBMIT_TRANSITION_EASING)

export interface WithdrawAddressScreenProps {
  /** Names the amount already confirmed, e.g. `Withdraw $100`. */
  title: string
  /** The typed recipient. */
  recipient: string
  onChangeRecipient: (value: string) => void
  /** Whether the recipient is an address at all; `undefined` while empty. */
  isAddress: boolean | undefined
  onPaste: () => void
  /** A clipboard read is out — the Paste action is held down until it lands. */
  isPasting?: boolean
  /** Omit to drop the connected-wallet shortcut. */
  onUseConnected?: () => void
  /** A reason the address can't be used, past being well-formed. */
  errorText?: string
  /** Non-blocking caution, e.g. a flagged-address notice. */
  warningText?: string
  /** What the withdrawal is received as, and the picker for it. */
  destinationToken: WithdrawalToken | undefined
  onPressReceive: () => void
  /**
   * What the recipient gets, in the currency the screen leads with. `undefined`
   * until the quote settles.
   */
  usdLabel: string | undefined
  /** The same amount in the token that arrives, e.g. `99.78 USDC`. */
  tokenLabel: string | undefined
  onPressBreakdown: () => void
  /** The quote couldn't be got — shown where the figure would be. */
  quoteErrorText?: string
  canSubmit: boolean
  /** The signature is in flight, or the quote it needs isn't settled. */
  isBusy: boolean
  /** The signature is out, or has landed and the outcome is taking over. */
  isSubmitting: boolean
  onSubmit: () => void
  /** An unrecoverable failure from the last attempt. */
  executionErrorText?: string
  onBack: () => void
  onClose: () => void
}

/**
 * Step two of the crypto withdrawal: where the money goes, and what it arrives
 * as. The amount was taken on the step before and is named in the title.
 */
export function WithdrawAddressScreen({
  title,
  recipient,
  onChangeRecipient,
  isAddress,
  onPaste,
  isPasting,
  onUseConnected,
  errorText,
  warningText,
  destinationToken,
  onPressReceive,
  usdLabel,
  tokenLabel,
  onPressBreakdown,
  quoteErrorText,
  canSubmit,
  isBusy,
  isSubmitting,
  onSubmit,
  executionErrorText,
  onBack,
  onClose,
}: WithdrawAddressScreenProps) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  const { height: windowHeight } = useWindowDimensions()
  const height = useFullHeightStep(windowHeight)
  const insets = useSafeAreaInsets()
  // The screen is already the whole window, so the sheet can't lift it: the
  // footer is what has to clear the keyboard.
  const keyboardHeight = useKeyboardHeight(true)
  const footerErrorText = executionErrorText ?? quoteErrorText

  return (
    <Box
      style={{
        height,
        paddingTop: insets.top,
        backgroundColor: theme.background.modal,
      }}
    >
      {isSubmitting ? null : (
        <AnimatedBox exiting={FADE_SLIDE_UP_EXITING}>
          <TopBar title={title} onBack={onBack} onClose={onClose} />
        </AnimatedBox>
      )}

      {isSubmitting ? null : (
        <AnimatedBox exiting={FADE_SLIDE_UP_EXITING} style={styles.body}>
          <ReceiveAsRow
            destinationToken={destinationToken}
            onPress={onPressReceive}
          />
          <WalletAddressInput
            value={recipient}
            onChangeText={onChangeRecipient}
            isAddress={isAddress}
            onPaste={onPaste}
            isPasting={isPasting}
            onUseConnected={onUseConnected}
            errorText={errorText}
            warningText={warningText}
          />
        </AnimatedBox>
      )}

      {/* Only an address gets a figure: there is nothing to quote against one
          that isn't, and a number beside "no wallet found" reads as a promise.
          Its band grows to fill the nav and inputs' place once withdrawing
          clears them out. */}
      <AnimatedBox
        layout={SUBMIT_TRANSITION}
        flex={1}
        justifyContent="center"
        style={styles.summary}
      >
        {isAddress ? (
          <AnimatedBox layout={SUBMIT_TRANSITION}>
            <ReceiveAmountSummary
              label={t('withdrawAddress.youllReceive')}
              usdLabel={usdLabel}
              tokenLabel={tokenLabel}
              // Nothing to break down until there is a figure.
              onPressBreakdown={usdLabel ? onPressBreakdown : undefined}
            />
          </AnimatedBox>
        ) : null}
      </AnimatedBox>

      {/* The way to send it waits on an address too. */}
      {recipient === '' ? null : (
        <AnimatedBox
          layout={SUBMIT_TRANSITION}
          style={[
            styles.footer,
            {
              // Withdrawing drops the keyboard, so stop clearing it in the same
              // commit that frees the space above: waiting for the hide event
              // would relayout the column a second time, mid-transition.
              marginBottom:
                keyboardHeight > 0 && !isSubmitting
                  ? keyboardHeight + FOOTER_KEYBOARD_GAP
                  : footerBottomMargin(insets.bottom),
            },
          ]}
        >
          {/* The last attempt's failure outranks a missing price: it is about
              money that already moved. */}
          {footerErrorText ? (
            <Text
              variant="detail"
              style={[
                styles.footerError,
                { color: theme.content.elements.error },
              ]}
            >
              {footerErrorText}
            </Text>
          ) : null}
          <PillButton
            label={
              isSubmitting
                ? t('withdrawAddress.withdrawing')
                : t('withdrawAddress.withdrawNow')
            }
            icon={
              isSubmitting ? (
                <ActivityIndicator
                  size="small"
                  color={theme.content.button.primary.default}
                />
              ) : undefined
            }
            disabled={!canSubmit || isBusy}
            onPress={() => {
              // The field outlives the press by the exit's length, and would
              // hold the keyboard over the footer until it unmounts.
              Keyboard.dismiss()
              onSubmit()
            }}
          />
        </AnimatedBox>
      )}
    </Box>
  )
}

// Measured off the design (a 402×778 content area above the home indicator).
const BODY_HORIZONTAL_MARGIN = 20
const FOOTER_KEYBOARD_GAP = 12

const styles = StyleSheet.create({
  body: {
    marginHorizontal: BODY_HORIZONTAL_MARGIN,
    paddingTop: 12,
    gap: 16,
  },
  summary: { paddingHorizontal: BODY_HORIZONTAL_MARGIN, paddingVertical: 12 },
  footer: { marginHorizontal: BODY_HORIZONTAL_MARGIN, gap: 12 },
  footerError: { textAlign: 'center' },
})
